import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter, getMaxDateForAge } from '@repo/toolkit/date'
import { MIN_AGE_REQUIRED } from '@repo/toolkit/min-age-required'
import Typography from '@repo/ui/typography'
import { isAfter } from 'date-fns'
import { useForm } from 'react-hook-form'

import { GenderEnum, genderEnumMapping } from '../../../lib/types/enums/common/gender.enum'
import { HostFamilyRelativeInput } from '../../../lib/types/models/host-family-relative/host-family-relative-input'
import { BaseFormProps } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledDateStringInput from '../../elements/inputs/controlled-date-string-input'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledRadioInput from '../../elements/inputs/controlled-radio-input'
import ValidationButtons from '../../elements/validation-buttons'
import { hostFamilyRelativeFormSchema } from './utils'

type Props = BaseFormProps<HostFamilyRelativeInput, true> & {
  className?: string
}
const HostFamilyChildForm = ({ className, defaultValues, ...validationButtonProps }: Props) => {
  const form = useForm<HostFamilyRelativeInput>({
    resolver: zodResolver(hostFamilyRelativeFormSchema),
    defaultValues,
    mode: FORM_MODE
  })

  const genderMapping = genderEnumMapping()

  const birthDate = form.watch('birthDate')

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <div className={cn('flex flex-col gap-4', className)}>
          <div>
            <Typography variant="label-semibold" color={tailwindColors.grey.DEFAULT}>
              Gender : *
            </Typography>
            <div className="h-2" />
            <div className="flex gap-5">
              {Object.values(GenderEnum).map((gender) => (
                <FormField
                  key={gender}
                  control={form.control}
                  name="gender"
                  render={({ field }) => {
                    return (
                      <FormItem>
                        <FormControl>
                          <ControlledRadioInput
                            {...field}
                            label={genderMapping[gender]}
                            checked={field.value === gender}
                            onCheck={() => field.onChange(gender)}
                          />
                        </FormControl>
                      </FormItem>
                    )
                  }}
                />
              ))}
            </div>
          </div>
          <div className="flex gap-3">
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormControl>
                    <ControlledInput {...field} required type="text" label="Last name" />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormControl>
                    <ControlledInput {...field} required type="text" label="First name" />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
          <div className="flex gap-3">
            <FormField
              control={form.control}
              name="birthDate"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <ControlledDateStringInput
                      {...field}
                      required
                      mode="over"
                      label="Date of birth"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <div className="bg-white-dark p-3 rounded flex items-center justify-center cursor-not-allowed">
              <Typography variant="label-medium" color={tailwindColors.grey.lighter}>
                {!birthDate || isAfter(birthDate, getMaxDateForAge(MIN_AGE_REQUIRED))
                  ? 'Age'
                  : DateFormatter.calculateAge(birthDate)}
              </Typography>
            </div>
          </div>
          <FormField
            control={form.control}
            name="relationToFamily"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormControl>
                  <ControlledInput
                    {...field}
                    required
                    type="text"
                    label="Relationship to the family"
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <ValidationButtons {...validationButtonProps} hasBorderTop />
      </form>
    </Form>
  )
}

export default HostFamilyChildForm
